import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="relative min-h-screen flex flex-col">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 no-underline">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
              Kakobuy <span className="text-primary-500">Finds Baby</span>
            </span>
          </a>

          {/* Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Home
            </a>
            <Link to="/kakobuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Guides
            </Link>
            <Link to="/kakobuy-vs-competitors" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Compare
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg py-2 shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`text-xl text-foreground-950 ${dropdownOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Buy It Now CTA */}
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
        </div>

        {/* Mobile dropdown - Full Screen Overlay */}
        {dropdownOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-background-50 overflow-y-auto">
            <div className="sticky top-0 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 px-4 py-4 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">Menu</span>
              <button
                onClick={() => setDropdownOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-xl text-foreground-800"></i>
              </button>
            </div>

            <div className="px-4 py-6">
              {/* Primary Nav Links */}
              <div className="flex flex-col gap-1 mb-8">
                <a href="/" onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50">
                    <i className="ri-home-line text-primary-500 text-lg"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Home</span>
                </a>
                <Link to="/kakobuy-spreadsheet-guide" onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50">
                    <i className="ri-book-open-line text-primary-500 text-lg"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Complete Guide</span>
                </Link>
                <Link to="/kakobuy-vs-competitors" onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50">
                    <i className="ri-scales-line text-accent-500 text-lg"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">vs Competitors</span>
                </Link>
              </div>

              {/* Categories Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 px-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50">
                    <i className="ri-grid-line text-accent-500 text-lg"></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-900 uppercase tracking-wider">Browse Categories</p>
                </div>
                <div className="flex flex-col gap-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 group-hover:bg-primary-50 transition-colors">
                        <i className={`${cat.icon} text-foreground-600 group-hover:text-primary-500 text-base transition-colors`}></i>
                      </div>
                      <span className="text-sm text-foreground-700 group-hover:text-foreground-900 transition-colors">{cat.name}</span>
                      <div className="w-4 h-4 flex items-center justify-center ml-auto">
                        <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Mobile CTA */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-base"></i>
                </div>
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="flex-1 flex flex-col lg:flex-row items-center pt-16 md:pt-18 pb-12 md:pb-16">
        {/* Left Column */}
        <div className="w-full lg:w-[38%] px-4 md:px-6 lg:pl-10 xl:pl-16 pt-10 md:pt-16 lg:pt-0">
          <p className="text-xs md:text-sm font-semibold text-primary-500 uppercase tracking-widest mb-4">
            Kakobuy Spreadsheet Database
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-[1.15] mb-2">
            Kakobuy Spreadsheet
          </h1>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-700 leading-[1.2] mb-6">
            Best Finds Database &amp; Shopping Guide
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-md">
            Find thousands of trending products, organized categories, comparison guides, and updated kakobuy spreadsheet resources designed to help you shop smarter.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 border-2 border-foreground-200 text-foreground-800 px-6 py-3 rounded-full text-sm font-semibold hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Visit Main Website
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-4 mt-8">
            {['Updated Product Database', 'Organized Shopping Categories', 'Detailed Comparison Guides', 'Safety First Resources'].map((badge) => (
              <div key={badge} className="flex items-center gap-2 text-xs text-foreground-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-primary-500 text-sm"></i>
                </div>
                {badge}
              </div>
            ))}
          </div>
        </div>

        {/* Middle Column - Large Image */}
        <div className="w-full lg:w-[32%] px-4 md:px-6 mt-10 lg:mt-0">
          <div className="relative w-full aspect-[4/5] max-w-md mx-auto lg:max-w-none rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20model%20wearing%20coordinated%20outfit%20with%20sneakers%20and%20accessories%20against%20warm%20beige%20studio%20backdrop%2C%20editorial%20fashion%20photography%2C%20soft%20diffused%20lighting%2C%20cinematic%20color%20grading%2C%20high%20end%20magazine%20quality%2C%20clean%20composition&width=800&height=1000&seq=hero-main-model-v3&orientation=portrait"
              alt="Kakobuy Spreadsheet Fashion Editorial"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Right Column - Trending Grid */}
        <div className="w-full lg:w-[30%] px-4 md:px-6 lg:pr-10 xl:pr-16 mt-10 lg:mt-0">
          <div className="flex items-center gap-2 mb-5">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Trending Now</h3>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-primary-500 text-lg"></i>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 relative aspect-[2/1] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Pair%20of%20designer%20sneakers%20on%20white%20marble%20surface%20with%20small%20potted%20plant%20and%20coffee%20cup%2C%20warm%20morning%20sunlight%20from%20window%2C%20minimalist%20lifestyle%20photography%2C%20soft%20shadows%2C%20clean%20modern%20aesthetic&width=800&height=400&seq=hero-trend-sneakers-v3&orientation=landscape"
                alt="Trending Sneakers"
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute bottom-3 left-3 bg-background-50/90 text-foreground-800 text-xs font-medium px-3 py-1 rounded-full">Shoes</span>
            </div>
            <div className="relative aspect-square rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Stack%20of%20neatly%20folded%20designer%20hoodies%20in%20neutral%20earth%20tones%20on%20wooden%20shelf%2C%20warm%20ambient%20lighting%2C%20minimalist%20clothing%20display%2C%20textured%20fabric%20details%2C%20boutique%20retail%20aesthetic%2C%20clean%20and%20organized&width=400&height=400&seq=hero-trend-hoodies-v3&orientation=squarish"
                alt="Trending Hoodies"
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute bottom-2 left-2 bg-background-50/90 text-foreground-800 text-xs font-medium px-2.5 py-1 rounded-full">Hoodies</span>
            </div>
            <div className="relative aspect-square rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20designer%20crossbody%20bag%20hanging%20on%20brass%20door%20handle%20with%20sunlight%20streaming%20through%2C%20minimal%20interior%20background%2C%20warm%20golden%20hour%20lighting%2C%20luxurious%20editorial%20style%2C%20soft%20bokeh%20effect&width=400&height=400&seq=hero-trend-bags-v3&orientation=squarish"
                alt="Trending Bags"
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute bottom-2 left-2 bg-background-50/90 text-foreground-800 text-xs font-medium px-2.5 py-1 rounded-full">Bags</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}